import { c as _c } from "react/compiler-runtime";
import { BusPictogram, FerryPictogram, InfoCircleSolid, OvernightBus, OvernightFerry, RoutePictogram, SleeperTrainPictogram, Taxi, TicketSolid, TrainPictogram, Walking } from '@byway/icons';
import { format, parseISO } from 'date-fns';
import { useTranslations } from 'next-intl';
import { Fragment } from 'react';
import { formatDurationHM } from 'utils/DateFormats';
import { ChangeTravelTimesField } from '~/components/TripOverview/transportPage/ChangeTravelTimesField';
import { visuallyHidden } from '~/components/Utilities';
import { getTicketsIncludedKey, transportModeKeys } from '~/constants/transport-modes';
import type { TripSection } from '~/stores/tpv2/journeyStore';
import type { LegTransformed, Transport } from '~/types/JourneyItem.types';
import { ItineraryCard } from './ItineraryCard';
import styles from './LegCard.module.css';
type LegCardProps = LegTransformed & {
  section: TripSection;
  dayNumber?: number;
};
const MODE_ICON: Partial<Record<Transport, typeof TrainPictogram>> = {
  RAIL: TrainPictogram,
  TRAIN: TrainPictogram,
  TRAM: TrainPictogram,
  JOURNEY: TrainPictogram,
  SLEEPER: SleeperTrainPictogram,
  BUS: BusPictogram,
  COACH: BusPictogram,
  OVERNIGHT_BUS: OvernightBus,
  FERRY: FerryPictogram,
  OVERNIGHT_FERRY: OvernightFerry,
  TAXI: Taxi,
  WALK: Walking
};
function formatDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'd MMM yy');
  } catch {
    return dateStr;
  }
}
function formatStationLabel(city: string, terminalId: string | null): string {
  return terminalId ? `${city} (${terminalId.split('-')[1]?.toLowerCase() ?? terminalId})` : city;
}
const LegCard = t0 => {
  const $ = _c(115);
  const {
    itemId,
    origin,
    destination,
    viaStops,
    startDate,
    departureTimeText,
    arrivalTimeText,
    duration,
    fromTerminalId,
    fromTerminalName,
    toTerminalId,
    toTerminalName,
    interrail,
    channel,
    modeOfTransports,
    section,
    dayNumber
  } = t0;
  const t = useTranslations("journey");
  const tRoot = useTranslations();
  const tAria = useTranslations("aria");
  let t1;
  if ($[0] !== viaStops) {
    t1 = viaStops ?? [];
    $[0] = viaStops;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== destination || $[3] !== origin || $[4] !== t1) {
    t2 = [origin, ...t1, destination];
    $[2] = destination;
    $[3] = origin;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const cities = t2;
  let T0;
  let destinationStation;
  let formattedDate;
  let formattedDuration;
  let hasTravelTimes;
  let originStation;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let ticketsRow;
  if ($[6] !== arrivalTimeText || $[7] !== channel || $[8] !== cities || $[9] !== dayNumber || $[10] !== departureTimeText || $[11] !== destination || $[12] !== duration || $[13] !== fromTerminalId || $[14] !== fromTerminalName || $[15] !== interrail || $[16] !== modeOfTransports || $[17] !== origin || $[18] !== section || $[19] !== startDate || $[20] !== t || $[21] !== tAria || $[22] !== tRoot || $[23] !== toTerminalId || $[24] !== toTerminalName || $[25] !== viaStops) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const modes = modeOfTransports.filter(Boolean) as Transport[];
      const displayModes = modes.length > 0 ? modes : ["RAIL"] as Transport[];
      const showTickets = interrail || channel === "RAIL";
      let t8;
      if ($[38] !== viaStops) {
        t8 = viaStops ?? [];
        $[38] = viaStops;
        $[39] = t8;
      } else {
        t8 = $[39];
      }
      const ticketsIncludedKey = getTicketsIncludedKey(modes, t8.length > 0);
      ticketsRow = showTickets && <div className={styles.detailRow}><TicketSolid size="small" decorative={true} /><span>{t(ticketsIncludedKey)}</span></div>;
      if (section === "overview") {
        const HeaderIcon = displayModes.length > 1 ? RoutePictogram : MODE_ICON[displayModes[0]] ?? TrainPictogram;
        let t9;
        if ($[40] !== HeaderIcon) {
          t9 = <HeaderIcon size="small" decorative={true} />;
          $[40] = HeaderIcon;
          $[41] = t9;
        } else {
          t9 = $[41];
        }
        let t10;
        if ($[42] !== destination || $[43] !== t) {
          t10 = t("itinerary_travel_to", {
            place: destination
          });
          $[42] = destination;
          $[43] = t;
          $[44] = t10;
        } else {
          t10 = $[44];
        }
        let t11;
        if ($[45] !== dayNumber || $[46] !== t) {
          t11 = dayNumber === undefined ? undefined : t("itinerary_day", {
            day: dayNumber
          });
          $[45] = dayNumber;
          $[46] = t;
          $[47] = t11;
        } else {
          t11 = $[47];
        }
        let t12;
        if ($[48] !== t10 || $[49] !== t11 || $[50] !== t9) {
          t12 = <ItineraryCard.Header icon={t9} title={t10} meta={t11} />;
          $[48] = t10;
          $[49] = t11;
          $[50] = t9;
          $[51] = t12;
        } else {
          t12 = $[51];
        }
        let t13;
        if ($[52] !== cities[0]) {
          t13 = <span className={styles.routeCity}>{cities[0]}</span>;
          $[52] = cities[0];
          $[53] = t13;
        } else {
          t13 = $[53];
        }
        const t14 = cities.slice(1).map((city, index) => {
          const Icon = MODE_ICON[displayModes[index]] ?? TrainPictogram;
          return <Fragment key={cities.slice(0, index + 2).join("\u2192")}><span className={styles.routeConnector} aria-hidden="true"><span className={styles.routeDivider} /><Icon size="small" decorative={true} /><span className={styles.routeDivider} /></span><span className={visuallyHidden}>{` ${tAria("to")} `}</span><span className={styles.routeCity}>{city}</span></Fragment>;
        });
        let t15;
        if ($[54] !== t13 || $[55] !== t14) {
          t15 = <p className={styles.route}>{t13}{t14}</p>;
          $[54] = t13;
          $[55] = t14;
          $[56] = t15;
        } else {
          t15 = $[56];
        }
        let t16;
        if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
          t16 = <hr className={styles.divider} />;
          $[57] = t16;
        } else {
          t16 = $[57];
        }
        let t17;
        if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
          t17 = <InfoCircleSolid size="small" decorative={true} />;
          $[58] = t17;
        } else {
          t17 = $[58];
        }
        let t18;
        if ($[59] !== t) {
          t18 = t("overview_choose_times");
          $[59] = t;
          $[60] = t18;
        } else {
          t18 = $[60];
        }
        let t19;
        if ($[61] !== t18) {
          t19 = <div className={styles.detailRow}>{t17}<span>{t18}</span></div>;
          $[61] = t18;
          $[62] = t19;
        } else {
          t19 = $[62];
        }
        let t20;
        if ($[63] !== t19 || $[64] !== ticketsRow) {
          t20 = <div className={styles.content}>{ticketsRow}{t19}</div>;
          $[63] = t19;
          $[64] = ticketsRow;
          $[65] = t20;
        } else {
          t20 = $[65];
        }
        let t21;
        if ($[66] !== t12 || $[67] !== t15 || $[68] !== t20) {
          t21 = <ItineraryCard variant="leg">{t12}{t15}{t16}{t20}</ItineraryCard>;
          $[66] = t12;
          $[67] = t15;
          $[68] = t20;
          $[69] = t21;
        } else {
          t21 = $[69];
        }
        t7 = t21;
        break bb0;
      }
      let t9;
      if ($[70] !== startDate) {
        t9 = formatDate(startDate);
        $[70] = startDate;
        $[71] = t9;
      } else {
        t9 = $[71];
      }
      formattedDate = t9;
      let t10;
      if ($[72] !== duration) {
        t10 = duration ? formatDurationHM({
          minutes: duration
        }) : "";
        $[72] = duration;
        $[73] = t10;
      } else {
        t10 = $[73];
      }
      formattedDuration = t10;
      let t11;
      if ($[74] !== fromTerminalId || $[75] !== fromTerminalName || $[76] !== origin) {
        t11 = fromTerminalName ?? formatStationLabel(origin, fromTerminalId);
        $[74] = fromTerminalId;
        $[75] = fromTerminalName;
        $[76] = origin;
        $[77] = t11;
      } else {
        t11 = $[77];
      }
      originStation = t11;
      let t12;
      if ($[78] !== destination || $[79] !== toTerminalId || $[80] !== toTerminalName) {
        t12 = toTerminalName ?? formatStationLabel(destination, toTerminalId);
        $[78] = destination;
        $[79] = toTerminalId;
        $[80] = toTerminalName;
        $[81] = t12;
      } else {
        t12 = $[81];
      }
      destinationStation = t12;
      hasTravelTimes = Boolean(departureTimeText || arrivalTimeText);
      let t13;
      if ($[82] !== cities || $[83] !== tAria) {
        t13 = cities.map((city_0, index_0) => <Fragment key={cities.slice(0, index_0 + 1).join("\u2192")}>{index_0 > 0 && <><span aria-hidden="true"> → </span><span className={visuallyHidden}>{` ${tAria("to")} `}</span></>}{city_0}</Fragment>);
        $[82] = cities;
        $[83] = tAria;
        $[84] = t13;
      } else {
        t13 = $[84];
      }
      const titleContent = t13;
      const PrimaryModeIcon = MODE_ICON[displayModes[0]] ?? TrainPictogram;
      const isTransport = section === "transport";
      T0 = ItineraryCard;
      if ($[85] !== PrimaryModeIcon || $[86] !== isTransport || $[87] !== titleContent) {
        t6 = isTransport && <ItineraryCard.Header icon={<PrimaryModeIcon size="small" decorative={true} />} title={titleContent} />;
        $[85] = PrimaryModeIcon;
        $[86] = isTransport;
        $[87] = titleContent;
        $[88] = t6;
      } else {
        t6 = $[88];
      }
      t3 = styles.body;
      if ($[89] !== isTransport || $[90] !== titleContent) {
        t4 = !isTransport && <p className={styles.title}>{titleContent}</p>;
        $[89] = isTransport;
        $[90] = titleContent;
        $[91] = t4;
      } else {
        t4 = $[91];
      }
      t5 = !isTransport && <div className={styles.modes}>{displayModes.map((mode, index_1) => {
          const Icon_0 = MODE_ICON[mode] ?? TrainPictogram;
          return <Fragment key={displayModes.slice(0, index_1 + 1).join("-")}>{index_1 > 0 && <span aria-hidden="true" className={styles.modeArrow}>→</span>}<span className={styles.mode}><Icon_0 size="small" decorative={true} />{transportModeKeys[mode] ? tRoot(transportModeKeys[mode].labelKey) : mode}</span></Fragment>;
        })}</div>;
    }
    $[6] = arrivalTimeText;
    $[7] = channel;
    $[8] = cities;
    $[9] = dayNumber;
    $[10] = departureTimeText;
    $[11] = destination;
    $[12] = duration;
    $[13] = fromTerminalId;
    $[14] = fromTerminalName;
    $[15] = interrail;
    $[16] = modeOfTransports;
    $[17] = origin;
    $[18] = section;
    $[19] = startDate;
    $[20] = t;
    $[21] = tAria;
    $[22] = tRoot;
    $[23] = toTerminalId;
    $[24] = toTerminalName;
    $[25] = viaStops;
    $[26] = T0;
    $[27] = destinationStation;
    $[28] = formattedDate;
    $[29] = formattedDuration;
    $[30] = hasTravelTimes;
    $[31] = originStation;
    $[32] = t3;
    $[33] = t4;
    $[34] = t5;
    $[35] = t6;
    $[36] = t7;
    $[37] = ticketsRow;
  } else {
    T0 = $[26];
    destinationStation = $[27];
    formattedDate = $[28];
    formattedDuration = $[29];
    hasTravelTimes = $[30];
    originStation = $[31];
    t3 = $[32];
    t4 = $[33];
    t5 = $[34];
    t6 = $[35];
    t7 = $[36];
    ticketsRow = $[37];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[92] !== t3 || $[93] !== t4 || $[94] !== t5 || $[95] !== ticketsRow) {
    t8 = <div className={t3}>{t4}{t5}{ticketsRow}</div>;
    $[92] = t3;
    $[93] = t4;
    $[94] = t5;
    $[95] = ticketsRow;
    $[96] = t8;
  } else {
    t8 = $[96];
  }
  let t9;
  if ($[97] !== arrivalTimeText || $[98] !== departureTimeText || $[99] !== destination || $[100] !== destinationStation || $[101] !== formattedDate || $[102] !== formattedDuration || $[103] !== hasTravelTimes || $[104] !== itemId || $[105] !== origin || $[106] !== originStation || $[107] !== section || $[108] !== startDate) {
    t9 = section === "transport" && hasTravelTimes && <ItineraryCard.Content><ItineraryCard.TrainTimes date={formattedDate} departureTime={departureTimeText} arrivalTime={arrivalTimeText} originStation={originStation} destinationStation={destinationStation} duration={formattedDuration} /><ChangeTravelTimesField itemId={itemId} startDate={startDate} origin={origin} destination={destination} departureTimeText={departureTimeText} arrivalTimeText={arrivalTimeText} /></ItineraryCard.Content>;
    $[97] = arrivalTimeText;
    $[98] = departureTimeText;
    $[99] = destination;
    $[100] = destinationStation;
    $[101] = formattedDate;
    $[102] = formattedDuration;
    $[103] = hasTravelTimes;
    $[104] = itemId;
    $[105] = origin;
    $[106] = originStation;
    $[107] = section;
    $[108] = startDate;
    $[109] = t9;
  } else {
    t9 = $[109];
  }
  let t10;
  if ($[110] !== T0 || $[111] !== t6 || $[112] !== t8 || $[113] !== t9) {
    t10 = <T0>{t6}{t8}{t9}</T0>;
    $[110] = T0;
    $[111] = t6;
    $[112] = t8;
    $[113] = t9;
    $[114] = t10;
  } else {
    t10 = $[114];
  }
  return t10;
};
export { LegCard };